'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { ImageZoom } from '@/components/kibo-ui/image-zoom';
import {
  getStoredPvcCockpitLanguage,
  PVC_COCKPIT_LANGUAGES,
  PVC_COCKPIT_SALE,
  type PvcCockpitLanguage,
  storePvcCockpitLanguage,
} from '@/features/sales/pvc-cockpit/pvc-cockpit.data';
import { playSfx } from '@/hooks/use-sound';
import { cn } from '@/lib/utils';

import { HotmartCheckout } from './hotmart-checkout';

const LANGUAGE_LABELS: Record<PvcCockpitLanguage, string> = {
  'pt-BR': 'PT',
  en: 'EN',
  es: 'ES',
  fr: 'FR',
};

const LANGUAGE_NAMES: Record<PvcCockpitLanguage, string> = {
  'pt-BR': 'Português (Brasil)',
  en: 'English',
  es: 'Español',
  fr: 'Français',
};

const GALLERY_IMAGES = Array.from(
  { length: PVC_COCKPIT_SALE.gallery.imageCount },
  (_, i) => `${PVC_COCKPIT_SALE.gallery.basePath}/${i + 1}.png`
);

export function PvcCockpitContent() {
  const [lang, setLang] = useState<PvcCockpitLanguage>('en');

  useEffect(() => {
    const stored = getStoredPvcCockpitLanguage();
    if (stored) {
      setLang(stored);
    }
  }, []);

  const selectLang = (l: PvcCockpitLanguage) => {
    if (l !== lang) {
      playSfx('toggle');
    }
    setLang(l);
    storePvcCockpitLanguage(l);
  };

  const t = PVC_COCKPIT_SALE.copyByLang[lang];

  return (
    <div className="mx-auto min-h-svh md:max-w-3xl" lang={lang}>
      <div className="border-x border-edge">
        <fieldset className="flex justify-end gap-1 px-4 pt-2">
          <legend className="sr-only">Language</legend>
          {PVC_COCKPIT_LANGUAGES.map((l) => (
            <button
              key={l}
              type="button"
              data-sfx-managed=""
              onClick={() => selectLang(l)}
              aria-label={LANGUAGE_NAMES[l]}
              aria-pressed={lang === l}
              className={cn(
                'flex min-h-11 min-w-11 items-center justify-center rounded px-2 font-mono text-xs transition-colors',
                lang === l
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              )}
            >
              {LANGUAGE_LABELS[l]}
            </button>
          ))}
        </fieldset>

        <div className="screen-line-after px-4 pt-4 pb-4 text-center">
          <h1 className="text-3xl font-semibold md:text-4xl">{t.title}</h1>
          <p className="mt-2 text-lg text-muted-foreground">{t.subtitle}</p>
        </div>

        <div className="space-y-8 p-4 md:p-6">
          <div className="rounded-xl bg-gradient-to-br from-accent to-accent2 p-6 text-center">
            <p className="text-2xl font-bold md:text-3xl">{t.savings}</p>
            <p className="mt-2 text-muted-foreground">{t.savingsSubtitle}</p>
          </div>

          <div className="space-y-4 rounded-xl border border-edge bg-card p-6">
            <p className="text-center text-sm font-medium text-muted-foreground">
              {t.statsTitle}
            </p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="space-y-1">
                <p className="text-2xl font-bold text-primary md:text-3xl">
                  {t.statsSales}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.statsSalesLabel}
                </p>
              </div>
              <div className="space-y-1 border-x border-edge">
                <p className="text-2xl font-bold text-primary md:text-3xl">
                  {t.statsViews}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.statsViewsLabel}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-2xl font-bold text-primary md:text-3xl">
                  {t.statsReach}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.statsReachLabel}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-xl border-2 border-primary bg-gradient-to-br from-accent to-background p-6 text-center">
            <div>
              <p className="text-sm text-muted-foreground line-through">
                {t.priceFrom}
              </p>
              <p className="text-4xl font-bold text-primary">{t.price}</p>
              <p className="text-sm text-muted-foreground">{t.priceNote}</p>
            </div>
            <HotmartCheckout lang={lang} />
          </div>

          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-semibold">{t.galleryTitle}</h2>
              <p className="text-sm text-muted-foreground">
                {t.gallerySubtitle}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-3">
              {GALLERY_IMAGES.map((src, index) => (
                <ImageZoom key={src}>
                  <Image
                    src={src}
                    alt={t.galleryAlts[index] ?? 'PVC Cockpit'}
                    width={400}
                    height={300}
                    className="aspect-[4/3] w-full cursor-zoom-in rounded-lg border border-edge object-cover transition-transform hover:scale-[1.02]"
                    sizes="(max-width: 768px) 50vw, 240px"
                  />
                </ImageZoom>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t.learnTitle}</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-3">
                <span className="text-success">✓</span>
                <span>{t.learn1}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-success">✓</span>
                <span>{t.learn2}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-success">✓</span>
                <span>{t.learn3}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-success">✓</span>
                <span>{t.learn4}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-success">✓</span>
                <span>{t.learn5}</span>
              </li>
            </ul>
          </div>

          <div className="space-y-4 rounded-xl border border-edge bg-card p-6">
            <h2 className="text-xl font-semibold">{t.whyPvcTitle}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="font-medium">{t.benefit1Title}</p>
                <p className="text-sm text-muted-foreground">
                  {t.benefit1Desc}
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t.benefit2Title}</p>
                <p className="text-sm text-muted-foreground">
                  {t.benefit2Desc}
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t.benefit3Title}</p>
                <p className="text-sm text-muted-foreground">
                  {t.benefit3Desc}
                </p>
              </div>
              <div className="space-y-1">
                <p className="font-medium">{t.benefit4Title}</p>
                <p className="text-sm text-muted-foreground">
                  {t.benefit4Desc}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-edge bg-card p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold">{t.youtubeTitle}</h2>
              <p className="mt-2 text-muted-foreground">
                {t.youtubeDescription}
              </p>
            </div>
            <div className="aspect-video w-full overflow-hidden rounded-lg">
              <iframe
                src={`https://www.youtube.com/embed/${PVC_COCKPIT_SALE.youtube.videoId}`}
                title="PVC Cockpit DIY Tutorial"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="h-full w-full"
              />
            </div>
          </div>

          <div className="space-y-6 rounded-xl border-2 border-primary bg-gradient-to-br from-accent to-background p-6 text-center">
            <div>
              <p className="text-sm text-muted-foreground line-through">
                {t.priceFrom}
              </p>
              <p className="text-4xl font-bold text-primary">{t.price}</p>
              <p className="text-sm text-muted-foreground">{t.priceNote}</p>
            </div>

            <HotmartCheckout lang={lang} />

            <p className="text-xs text-muted-foreground">
              {t.securePayment}
              <br />
              {t.instantAccess}
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">{t.faqTitle}</h2>
            <div className="space-y-4">
              <div>
                <p className="font-medium">{t.faq1Question}</p>
                <p className="text-sm text-muted-foreground">{t.faq1Answer}</p>
              </div>
              <div>
                <p className="font-medium">{t.faq2Question}</p>
                <p className="text-sm text-muted-foreground">{t.faq2Answer}</p>
              </div>
              <div>
                <p className="font-medium">{t.faq3Question}</p>
                <p className="text-sm text-muted-foreground">{t.faq3Answer}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-edge bg-card p-6">
            <Image
              src="/images/victor-saisse-avatar.webp"
              alt="Victor Saisse"
              width={48}
              height={48}
              className="size-12 shrink-0 rounded-full"
            />
            <div className="space-y-0.5">
              <p className="font-medium">{t.bylineTitle}</p>
              <Link
                href="/"
                className="text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
              >
                {t.bylineCta}
              </Link>
            </div>
          </div>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
